'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import type {
  CubeCatalog,
  CubeSelections
} from '~/components/new-cars/Tdc/cubeSelections';
import { TDC_API_BASE } from '~/components/new-cars/Tdc/tdcApi';
import { useIsNative } from '~/context/common/DeviceContext';

export default function useTestDriveCenter() {
  const isNative = useIsNative();
  const searchParams = useSearchParams();
  const tdcParam = searchParams?.get('tdc') ?? null;
  const sessionParam = searchParams?.get('session') ?? null;

  const isActive = tdcParam === 'true' || Boolean(sessionParam);
  const session = sessionParam;

  const [isSent, setIsSent] = useState(false);
  const [tourData, setTourData] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const sendBuild = useCallback(
    async (build: {
      make: string;
      model: string;
      year: number;
      trim: string;
      color?: string;
      interior?: string;
      drivetrain?: string;
      options?: string[];
    }) => {
      if (!session) {
        setError('No TDC session. Please pair with the display first.');

        return false;
      }

      try {
        const res = await fetch(
          `${TDC_API_BASE}/api/build?session=${session}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...build,
              engineShort: 'Unknown',
              engine: 'Unknown',
              options: (build.options || []).map(o => ({ name: o, msrp: 0 }))
            })
          }
        );

        const data = await res.json();

        if (res.status === 403 || data.error) {
          setError(data.error || 'Session expired. Please re-pair.');

          return false;
        }

        if (data.ok) {
          setTourData(data.tour);
          setIsSent(true);

          // Redirect the phone to its take-home mobile tour page on the cube.
          // The big display keeps its own connection and stays paired.
          if (data.tourId && !isNative) {
            window.location.href = `${TDC_API_BASE}/m/${data.tourId}`;
          }

          return true;
        }

        return false;
      } catch (err) {
        setError('Failed to connect to the display. Please try again.');

        return false;
      }
    },
    [isNative, session]
  );

  const sendCube = useCallback(
    (payload: {
      catalog?: CubeCatalog | null;
      changed?: string[];
      make?: string | null;
      makeCount?: number;
      model?: string | null;
      models?: Array<{ count: number; model: string }>;
      screen?:
        | 'attract'
        | 'selectModel'
        | 'build'
        | 'trim'
        | 'lifestyle'
        | null;
      selections?: CubeSelections | null;
      trim?: string | null;
    }) => {
      // Best-effort, fire-and-forget: tell the paired cube display WHICH SCREEN
      // to show as the customer moves through the flow (selectModel → build →
      // trim), plus the make/model/trim context for the conveyor. On the
      // selectModel screen, `models` carries per-model inventory counts and
      // `makeCount` carries this make's total — the cube keeps the latest total
      // per make and sums them for its idle screen, so the fleet total fills in
      // and refreshes as makes are visited across sessions.
      // In the options stage, `screen` is omitted (null) for selections-only
      // updates: `selections` is the full current build (with `changed` listing
      // which fields just moved) and `catalog` is the one-shot list of every
      // available option, so the cube can prefetch images and render the live
      // build without changing screens.
      // Never block the UI or throw — a 403 is expected when no display is paired.
      if (!session) return;

      try {
        void fetch(`${TDC_API_BASE}/api/cube?session=${session}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            catalog: payload.catalog ?? null,
            changed: payload.changed ?? null,
            make: payload.make ?? null,
            makeCount: payload.makeCount ?? null,
            model: payload.model ?? null,
            models: payload.models ?? null,
            screen: payload.screen ?? null,
            selections: payload.selections ?? null,
            trim: payload.trim ?? null
          })
        }).catch(() => {});
      } catch {}
    },
    [session]
  );

  const startOver = useCallback(async () => {
    try {
      await fetch(
        `${TDC_API_BASE}/api/clear${session ? `?session=${session}` : ''}`,
        { method: 'POST' }
      );
    } catch {}
    if (isActive) {
      if (!isNative) {
        window.location.href = `/new${
          session ? `?session=${session}` : '?tdc=true'
        }`;
      }

      return;
    }
    setIsSent(false);
    setTourData(null);
    setError(null);
  }, [isActive, isNative, session]);

  const endSession = useCallback(async () => {
    try {
      await fetch(
        `${TDC_API_BASE}/api/idle${session ? `?session=${session}` : ''}`,
        { method: 'POST' }
      );
    } catch {}
    if (!isNative) window.location.href = '/new?tdc=true';
  }, [isNative, session]);

  return useMemo(
    () => ({
      isActive,
      session,
      cubeUrl: TDC_API_BASE,
      isPairing: isActive && !session,
      isSent,
      error,
      tourData,
      sendBuild,
      sendCube,
      startOver,
      endSession,
      setError
    }),
    [
      isActive,
      session,
      isSent,
      error,
      tourData,
      sendBuild,
      sendCube,
      startOver,
      endSession
    ]
  );
}
